// File: frontend/src/pages/admin/AdminPage.tsx
// src/pages/admin/AdminPage.tsx

import React, { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { 
  FaChartBar, 
  FaUsers, 
  FaFileAlt, 
  FaFutbol, 
  FaShieldAlt, 
  FaCog,
  FaBuilding,
  FaMapMarkerAlt,
  FaBan,
  FaFlag,
  FaHistory,
  FaUserGraduate,
  FaPenNib,
  FaDatabase,
  FaUser,
  FaAddressBook,
  FaHashtag,
  FaRandom,
  FaThumbtack,
  FaBook,
  FaGavel,
  FaChartLine,
} from "react-icons/fa";
import {
  AdminDashboard,
  AdminUsers,
  AdminMatches,
  AdminClubs,
  AdminTeams,
  AdminStadiums,
  AdminEntries,
  AdminBannedIps,
  AdminReports,
  AdminAuditLogs,
  AdminSettings,
  AdminMasterData,
  AdminPeople,
  AdminContact,
  AdminLegal,
  AdminCustomTopics,
  AdminRankBoard,
  AdminTopicOps,
  AdminPins,
  AdminManual,
  AdminTraffic,
} from "../../components/admin";
import "./AdminPage.css";

// Tab types for admin panel
type AdminTab = "dashboard" | "users" | "authors" | "rookies" | "entries" | "people" | "matches" | "teams" | "clubs" | "custom-topics" | "topic-ops" | "pins" | "stadiums" | "banned-ips" | "reports" | "audit-logs" | "master-data" | "settings" | "contact" | "legal" | "traffic" | "manual";

// All valid tab keys — used to validate the ?tab= deep link
const ADMIN_TABS: readonly AdminTab[] = ["dashboard", "users", "authors", "rookies", "entries", "people", "matches", "teams", "clubs", "custom-topics", "topic-ops", "pins", "stadiums", "banned-ips", "reports", "audit-logs", "master-data", "settings", "contact", "legal", "traffic", "manual"];

// Tabs that are rendered for ADMIN only — a moderator deep link to them is ignored
const ADMIN_ONLY_TABS: readonly AdminTab[] = ["audit-logs", "master-data", "settings", "contact", "legal", "traffic"];

// Resolve a ?tab= value to a tab the given role may open (null = ignore)
const resolveDeepLinkTab = (value: string | null, role: string | undefined): AdminTab | null => {
  if (!value || !(ADMIN_TABS as readonly string[]).includes(value)) return null;
  const tab = value as AdminTab;
  if (ADMIN_ONLY_TABS.includes(tab) && role?.toUpperCase() !== "ADMIN") return null;
  return tab;
};

// Check if user has admin role
const isAdmin = (role: string): boolean => {
  return role.toUpperCase() === "ADMIN";
};

// Check if user has admin or moderator role
const isAdminOrModerator = (role: string): boolean => {
  const upper = role.toUpperCase();
  return upper === "ADMIN" || upper === "MODERATOR";
};

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  // Deep link support: /admin?tab=custom-topics (used by the header's pending
  // suggestion alert). The initial value is read synchronously so the dashboard
  // is not mounted (and fetched) for nothing before switching.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<AdminTab>(
    () => resolveDeepLinkTab(requestedTab, user?.role) ?? "dashboard"
  );

  // Bumped on every deep link that arrives while the page is already open.
  // Used as the key of the tab content, so the target tab is remounted with its
  // default state — e.g. Gündem always lands on "Öneriler" + "Bekliyor" with a
  // fresh list, even if that tab was already open on "Başlıklar".
  const [deepLinkKey, setDeepLinkKey] = useState(0);
  const pageMountedRef = useRef(false);

  // Apply a deep link that arrives while the page is already open, then drop
  // the param so the next click on the same link is seen as a change again
  useEffect(() => {
    if (!requestedTab) return;
    const tab = resolveDeepLinkTab(requestedTab, user?.role);
    if (tab) {
      setActiveTab(tab);
      // On first mount the tab is already fresh — remounting would only refetch
      if (pageMountedRef.current) setDeepLinkKey((k) => k + 1);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [requestedTab, searchParams, setSearchParams, user?.role]);

  // Must stay below the deep-link effect: marks the page as mounted after the
  // initial deep link (if any) has been applied
  useEffect(() => {
    pageMountedRef.current = true;
  }, []);

  // Redirect to home if not authenticated or not admin/moderator
  if (!isAuthenticated || !user || !isAdminOrModerator(user.role)) {
    return <Navigate to="/" replace />;
  }

  const userIsAdmin = isAdmin(user.role);

  // Handle tab change
  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <div className="admin-tab-content">
            <AdminDashboard
              onNavigateToCustomTopics={() => handleTabChange("custom-topics")}
              onNavigateToReports={() => handleTabChange("reports")}
            />
          </div>
        );
      case "users":
        return (
          <div className="admin-tab-content">
            <AdminUsers isAdmin={userIsAdmin} />
          </div>
        );
      case "authors":
        return (
          <div className="admin-tab-content">
            {/* Same board component in "authors" mode — identical scoring & UI */}
            <AdminRankBoard isAdmin={userIsAdmin} mode="authors" />
          </div>
        );
      case "rookies":
        return (
          <div className="admin-tab-content">
            <AdminRankBoard isAdmin={userIsAdmin} />
          </div>
        );
      case "entries":
        return (
          <div className="admin-tab-content">
            <AdminEntries isAdmin={userIsAdmin} />
          </div>
        );
      case "people":
        return (
          <div className="admin-tab-content">
            <AdminPeople isAdmin={userIsAdmin} />
          </div>
        );
      case "matches":
        return (
          <div className="admin-tab-content">
            <AdminMatches isAdmin={userIsAdmin} />
          </div>
        );
      case "teams":
        return (
          <div className="admin-tab-content">
            <AdminTeams isAdmin={userIsAdmin} />
          </div>
        );
      case "clubs":
        return (
          <div className="admin-tab-content">
            <AdminClubs isAdmin={userIsAdmin} />
          </div>
        );
      case "custom-topics":
        return (
          <div className="admin-tab-content">
            <AdminCustomTopics isAdmin={userIsAdmin} />
          </div>
        );
      case "topic-ops":
        return (
          <div className="admin-tab-content">
            {/* Move and merge — both open to ADMIN + MODERATOR */}
            <AdminTopicOps />
          </div>
        );
      case "pins":
        return (
          <div className="admin-tab-content">
            {/* Sitewide topic + entry pins (ADMIN + MODERATOR) */}
            <AdminPins isAdmin={userIsAdmin} />
          </div>
        );
      case "stadiums":
        return (
          <div className="admin-tab-content">
            <AdminStadiums isAdmin={userIsAdmin} />
          </div>
        );
      case "banned-ips":
        return (
          <div className="admin-tab-content">
            <AdminBannedIps isAdmin={userIsAdmin} />
          </div>
        );
      case "reports":
        return (
          <div className="admin-tab-content">
            <AdminReports isAdmin={userIsAdmin} />
          </div>
        );
      case "audit-logs":
        return (
          <div className="admin-tab-content">
            <AdminAuditLogs isAdmin={userIsAdmin} />
          </div>
        );
      case "master-data":
        return (
          <div className="admin-tab-content">
            <AdminMasterData isAdmin={userIsAdmin} />
          </div>
        );
      case "settings":
        return (
          <div className="admin-tab-content">
            <AdminSettings isAdmin={userIsAdmin} />
          </div>
        );
      case "contact":
        return (
          <div className="admin-tab-content">
            <AdminContact isAdmin={userIsAdmin} />
          </div>
        );
      case "legal":
        return (
          <div className="admin-tab-content">
            {/* Terms / Privacy / Cookie texts — ADMIN only. These are
                commitments made by the operator, so MODERATOR is excluded
                here and in the backend service. */}
            <AdminLegal isAdmin={userIsAdmin} />
          </div>
        );
      case "traffic":
        return (
          <div className="admin-tab-content">
            {/* Server-log traffic report — ADMIN only, see AdminTraffic.tsx */}
            <AdminTraffic />
          </div>
        );
      case "manual":
        return (
          <div className="admin-tab-content">
            {/* Living usage manual — visible to ADMIN + MODERATOR. Live setting
                values are ADMIN-only, so the component degrades gracefully. */}
            <AdminManual isAdmin={userIsAdmin} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-container">
      {/* Page Header */}
      <div className="admin-header">
        <h1 className="admin-title">{t("admin_panel_title")}</h1>
        <span className="admin-user-badge">
          {user.username} ({userIsAdmin ? t("status_admin") : t("status_moderator")})
        </span>
      </div>

      {/* Horizontal Tab Bar */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => handleTabChange("dashboard")}
        >
          <FaChartBar className="admin-tab-icon" />
          <span>{t("admin_tab_dashboard")}</span>
        </button>

        <button
          className={`admin-tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => handleTabChange("users")}
        >
          <FaUsers className="admin-tab-icon" />
          <span>{t("admin_tab_users")}</span>
        </button>

        {/* Authors tab — immediately LEFT of the Rookies tab. Same ranked
            board and scoring for AUTHOR, MODERATOR and ADMIN users; ranks
            are admin-only and are never shown on profiles. */}
        <button
          className={`admin-tab ${activeTab === "authors" ? "active" : ""}`}
          onClick={() => handleTabChange("authors")}
        >
          <FaPenNib className="admin-tab-icon" />
          <span>{t("admin_tab_authors")}</span>
        </button>

        {/* Rookies tab — right after Users. Ranked promotion board for ROOKIE users,
            visible to both ADMIN and MODERATOR (both can review candidates). */}
        <button
          className={`admin-tab ${activeTab === "rookies" ? "active" : ""}`}
          onClick={() => handleTabChange("rookies")}
        >
          <FaUserGraduate className="admin-tab-icon" />
          <span>{t("admin_tab_rookies")}</span>
        </button>

        {/* Entries tab - wired to AdminEntries component */}
        <button
          className={`admin-tab ${activeTab === "entries" ? "active" : ""}`}
          onClick={() => handleTabChange("entries")}
        >
          <FaFileAlt className="admin-tab-icon" />
          <span>{t("admin_tab_entries")}</span>
        </button>

        {/* Custom topics tab - visible for both ADMIN and MODERATOR (suggestion review is a moderation task) */}
        <button
          className={`admin-tab ${activeTab === "custom-topics" ? "active" : ""}`}
          onClick={() => handleTabChange("custom-topics")}
        >
          <FaHashtag className="admin-tab-icon" />
          <span>{t("admin_tab_custom_topics")}</span>
        </button>

        {/* Move / merge tab — entry relocation for ADMIN + MODERATOR,
            topic merge restricted to ADMIN inside the component */}
        <button
          className={`admin-tab ${activeTab === "topic-ops" ? "active" : ""}`}
          onClick={() => handleTabChange("topic-ops")}
        >
          <FaRandom className="admin-tab-icon" />
          <span>{t("admin_tab_topic_ops")}</span>
        </button>

        {/* Pins tab — sitewide topic + entry pins for ADMIN + MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "pins" ? "active" : ""}`}
          onClick={() => handleTabChange("pins")}
        >
          <FaThumbtack className="admin-tab-icon" />
          <span>{t("admin_tab_pins", "Sabitlenenler")}</span>
        </button>

        {/* Matches tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "matches" ? "active" : ""}`}
          onClick={() => handleTabChange("matches")}
        >
          <FaFutbol className="admin-tab-icon" />
          <span>{t("admin_tab_matches")}</span>
        </button>

        {/* Clubs tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "clubs" ? "active" : ""}`}
          onClick={() => handleTabChange("clubs")}
        >
          <FaBuilding className="admin-tab-icon" />
          <span>{t("admin_tab_clubs")}</span>
        </button>

        {/* People tab - moved here, right after Tanımlar (entries) */}
        <button
          className={`admin-tab ${activeTab === "people" ? "active" : ""}`}
          onClick={() => handleTabChange("people")}
        >
          <FaUser className="admin-tab-icon" />
          <span>{t("admin_tab_people", "Kişiler")}</span>
        </button>

        {/* Teams tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "teams" ? "active" : ""}`}
          onClick={() => handleTabChange("teams")}
        >
          <FaShieldAlt className="admin-tab-icon" />
          <span>{t("admin_tab_teams")}</span>
        </button>

        {/* Stadiums tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "stadiums" ? "active" : ""}`}
          onClick={() => handleTabChange("stadiums")}
        >
          <FaMapMarkerAlt className="admin-tab-icon" />
          <span>{t("admin_tab_stadiums")}</span>
        </button>

        {/* Banned IPs tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "banned-ips" ? "active" : ""}`}
          onClick={() => handleTabChange("banned-ips")}
        >
          <FaBan className="admin-tab-icon" />
          <span>{t("admin_tab_banned_ips")}</span>
        </button>

        {/* Reports tab - visible for both ADMIN and MODERATOR */}
        <button
          className={`admin-tab ${activeTab === "reports" ? "active" : ""}`}
          onClick={() => handleTabChange("reports")}
        >
          <FaFlag className="admin-tab-icon" />
          <span>{t("admin_tab_reports")}</span>
        </button>

        {/* Audit log tab - ADMIN only */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "audit-logs" ? "active" : ""}`}
            onClick={() => handleTabChange("audit-logs")}
          >
            <FaHistory className="admin-tab-icon" />
            <span>{t("admin_tab_audit_logs")}</span>
          </button>
        )}

        {/* Master Data tab - ADMIN only */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "master-data" ? "active" : ""}`}
            onClick={() => handleTabChange("master-data")}
          >
            <FaDatabase className="admin-tab-icon" />
            <span>{t("admin_tab_master_data")}</span>
          </button>
        )}

        {/* Settings tab - ADMIN only */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => handleTabChange("settings")}
          >
            <FaCog className="admin-tab-icon" />
            <span>{t("admin_tab_settings")}</span>
          </button>
        )}

        {/* Contact tab - ADMIN only */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "contact" ? "active" : ""}`}
            onClick={() => handleTabChange("contact")}
          >
            <FaAddressBook className="admin-tab-icon" />
            <span>{t("admin_tab_contact")}</span>
          </button>
        )}

        {/* Legal texts tab - ADMIN only */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "legal" ? "active" : ""}`}
            onClick={() => handleTabChange("legal")}
          >
            <FaGavel className="admin-tab-icon" />
            <span>{t("admin_tab_legal", "Yasal Metinler")}</span>
          </button>
        )}

        {/* Visitors tab - ADMIN only. Opens the GoAccess report generated from
            the nginx access log; see ops/analytics/README.md for the server
            side, which must be set up before the button leads anywhere. */}
        {userIsAdmin && (
          <button
            className={`admin-tab ${activeTab === "traffic" ? "active" : ""}`}
            onClick={() => handleTabChange("traffic")}
          >
            <FaChartLine className="admin-tab-icon" />
            <span>{t("admin_tab_traffic", "Ziyaretçiler")}</span>
          </button>
        )}

        {/* Usage manual tab — always last. Visible to ADMIN and MODERATOR.
            Inline fallback label keeps the locale files untouched. */}
        <button
          className={`admin-tab ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => handleTabChange("manual")}
        >
          <FaBook className="admin-tab-icon" />
          <span>{t("admin_tab_manual", "Kullanım Kılavuzu")}</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content" key={deepLinkKey}>
        {renderTabContent()}
      </div>
    </div>
  );
};